import { RemoteSignButton } from '../components/RemoteSignButton';
import { AgencySignButton } from '../components/AgencySignButton';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, 
  Search, 
  Users,
  Phone,
  Mail,
  Pencil, 
  Trash2,
  CreditCard,
  Printer,
  Building2,
  Calendar,
  Home,
  FileText,
  Download,
  XCircle,
  FileCheck,
  RefreshCw,
  History,
  PenLine,
  ScanLine,
  Camera,
  Loader2,
  AlertTriangle,
  LogOut,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import LeaseDurationCounter from '../components/LeaseDurationCounter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api, { clientsAPI, propertiesAPI, rentPaymentsAPI, transactionsAPI, signaturesAPI } from '../lib/api';
import { KeysInventoryTab } from '../components/keys/KeysInventoryTab';
import { 
  formatCurrency, 
  formatDate,
  clientTypeLabels,
  bamakoDistricts,
  cn
} from '../lib/utils';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';
import { SyncBadge } from '../components/SyncBadge';
import { SignaturePad } from '../components/SignaturePad';
import SecureImage from '../components/ui/SecureImage';
import AttestationsHistoryModal from '../components/AttestationsHistoryModal';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const clientTypeColors = {
  acheteur: 'bg-blue-100 text-blue-800',
  locataire: 'bg-purple-100 text-purple-800',
  vendeur: 'bg-green-100 text-green-800',
  bailleur: 'bg-orange-100 text-orange-800',
};

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  client_type: 'acheteur',
  budget_min: '',
  budget_max: '',
  preferred_districts: [],
  notes: '',
  client_code: '', // Identifiant personnalisé
  // Identite etendue
  birth_date: '',
  birth_place: '',
  nationality: '',
  gender: '',
  marital_status: '',
  // Coordonnees
  address: '',
  // Profession & revenus
  profession: '',
  employer: '',
  monthly_income: '',
  // Contact d'urgence
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relation: '',
  // Garant
  guarantor_name: '',
  guarantor_phone: '',
  guarantor_address: '',
  guarantor_profession: '',
  // RIB
  bank_name: '',
  bank_account: '',
  bank_iban: '',
  // Rental fields (for locataire only)
  rental_property_id: '',
  rental_start_date: new Date(),
  rental_end_date: null,
  rental_amount: '',
  rental_charges: '',
  rental_currency: 'XOF',
  rental_term_type: 'terme_a_echoir', // terme_echu ou terme_a_echoir
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [rentalProperties, setRentalProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user.role);
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // OCR scan state for "Nouveau client" form
  const [scanning, setScanning] = useState(false);
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scannedFields, setScannedFields] = useState(null);
  const scanInputRef = useRef(null);
  const scanCameraInputRef = useRef(null);

  const handleScanId = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
      toast.error('Format non supporté. Utilisez JPG, PNG ou WEBP.');
      return;
    }
    setScanFile(file);
    setScanPreview(URL.createObjectURL(file));
    setScanning(true);
    try {
      const res = await clientsAPI.scanId(file);
      const data = res.data;
      if (!data.success) {
        toast.warning(data.message || 'Aucune information extraite. Saisissez manuellement.');
        setScannedFields(null);
        return;
      }
      const cf = data.client_form || {};
      // Normalize the extracted gender to M / F (Gemini may return "M", "F", "Male",
      // "Female", "Masculin", "Feminin", etc.)
      const rawGender = String(cf.gender || data.extracted?.gender || '').trim().toLowerCase();
      let normalizedGender = '';
      if (rawGender) {
        if (rawGender.startsWith('m') || rawGender.startsWith('h')) normalizedGender = 'M';
        else if (rawGender.startsWith('f')) normalizedGender = 'F';
      }
      setScannedFields({ ...cf, normalized_gender: normalizedGender });
      setFormData(prev => ({
        ...prev,
        name: prev.name || cf.name || '',
        birth_date: prev.birth_date || cf.date_of_birth || '',
        birth_place: prev.birth_place || cf.place_of_birth || '',
        nationality: prev.nationality || cf.nationality || '',
        gender: prev.gender || normalizedGender || '',
        address: prev.address || cf.address || '',
        id_type: prev.id_type || cf.id_doc_type || '',
        id_number: prev.id_number || cf.id_doc_number || '',
      }));
      toast.success('Champs pré-remplis à partir de la pièce d\'identité');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  };

  const resetScan = () => {
    setScanFile(null);
    setScannedFields(null);
    if (scanPreview) { URL.revokeObjectURL(scanPreview); setScanPreview(null); }
    if (scanInputRef.current) scanInputRef.current.value = '';
    if (scanCameraInputRef.current) scanCameraInputRef.current.value = '';
  };
  
  // Lease termination states
  const [isLeaseTerminateOpen, setIsLeaseTerminateOpen] = useState(false);
  const [clientToTerminate, setClientToTerminate] = useState(null);
  const [leaseTerminationReason, setLeaseTerminationReason] = useState('');
  const [leaseTerminationNotes, setLeaseTerminationNotes] = useState('');
  const [leaseEffectiveEndDate, setLeaseEffectiveEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [leaseEndProrata, setLeaseEndProrata] = useState(null);
  const [prorataLoading, setProrataLoading] = useState(false);
  const [createProrataPayment, setCreateProrataPayment] = useState(true);
  const [terminatingLease, setTerminatingLease] = useState(false);
  const [leaseStatuses, setLeaseStatuses] = useState({});

  // Identity documents states
  const [isIdDocsOpen, setIsIdDocsOpen] = useState(false);
  const [idDocsClient, setIdDocsClient] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [idDocs, setIdDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [idDocForm, setIdDocForm] = useState({
    doc_type: 'carte_identite', doc_number: '', date_naissance: '', date_debut_validite: '', date_fin_validite: '', notes: ''
  });
  const [idDocPreview, setIdDocPreview] = useState(null);

  // Client 360 summary
  const [clientSummary, setClientSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Historical payment states
  const [isHistoricalPaymentOpen, setIsHistoricalPaymentOpen] = useState(false);
  const [clientForPayment, setClientForPayment] = useState(null);
  const [historicalPaymentData, setHistoricalPaymentData] = useState({
    amount: '',
    currency: 'XOF',
    payment_date: new Date(),
    period_month: new Date().getMonth(),
    period_year: new Date().getFullYear(),
    payment_method: 'especes',
    reference: '',
    notes: '',
    generate_receipt: true
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [attestationsHistoryClient, setAttestationsHistoryClient] = useState(null);
  const [signClientId, setSignClientId] = useState(null);
  const [signTransactionId, setSignTransactionId] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, propertiesRes] = await Promise.all([
        clientsAPI.getAll(),
        propertiesAPI.getAll()
      ]);
      setClients(clientsRes.data);
      // Filter only rental properties that are available
      setRentalProperties(propertiesRes.data.filter(p => 
        p.transaction_type === 'location' && p.status === 'disponible'
      ));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (client = null) => {
    if (client) {
      setSelectedClient(client);
      // Load 360 summary in background
      setClientSummary(null);
      setLoadingSummary(true);
      api.get(`/clients/${client.id}/summary`)
        .then((r) => setClientSummary(r.data))
        .catch(() => setClientSummary(null))
        .finally(() => setLoadingSummary(false));
      setFormData({
        ...initialFormData,
        ...client,
        budget_min: client.budget_min?.toString() || '',
        budget_max: client.budget_max?.toString() || '',
        monthly_income: client.monthly_income?.toString() || '',
        client_code: client.client_code || '',
        birth_date: client.birth_date || '',
        birth_place: client.birth_place || '',
        nationality: client.nationality || '',
        marital_status: client.marital_status || '',
        address: client.address || '',
        profession: client.profession || '',
        employer: client.employer || '',
        emergency_contact_name: client.emergency_contact_name || '',
        emergency_contact_phone: client.emergency_contact_phone || '',
        emergency_contact_relation: client.emergency_contact_relation || '',
        guarantor_name: client.guarantor_name || '',
        guarantor_phone: client.guarantor_phone || '',
        guarantor_address: client.guarantor_address || '',
        guarantor_profession: client.guarantor_profession || '',
        bank_name: client.bank_name || '',
        bank_account: client.bank_account || '',
        bank_iban: client.bank_iban || '',
        // Reset rental fields for edit mode (not applicable)
        rental_property_id: '',
        rental_start_date: new Date(),
        rental_end_date: null,
        rental_amount: '',
        rental_charges: '',
        rental_currency: 'XOF',
        rental_term_type: 'terme_a_echoir',
      });
    } else {
      setSelectedClient(null);
      setFormData(initialFormData);
    }
    // Reset OCR scan state whenever the form is opened
    setScanFile(null);
    setScannedFields(null);
    if (scanPreview) { URL.revokeObjectURL(scanPreview); setScanPreview(null); }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedClient(null);
    setFormData(initialFormData);
    resetScan();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    // Validate rental fields if locataire with property selected
    if (formData.client_type === 'locataire' && formData.rental_property_id && !formData.rental_amount) {
      toast.error('Veuillez indiquer le montant du loyer');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        monthly_income: formData.monthly_income ? parseFloat(formData.monthly_income) : null,
        email: formData.email || null,
        client_code: formData.client_code?.trim() || null,
      };

      // Add rental fields for new tenants
      if (!selectedClient && formData.client_type === 'locataire' && formData.rental_property_id) {
        submitData.rental_property_id = formData.rental_property_id;
        submitData.rental_start_date = formData.rental_start_date.toISOString();
        submitData.rental_end_date = formData.rental_end_date ? formData.rental_end_date.toISOString() : null;
        submitData.rental_amount = formData.rental_amount ? parseFloat(formData.rental_amount) : null;
        submitData.rental_charges = formData.rental_charges ? parseFloat(formData.rental_charges) : null;
        submitData.rental_currency = formData.rental_currency;
        submitData.rental_term_type = formData.rental_term_type || 'terme_a_echoir';
      } else {
        // Remove rental fields if not applicable
        delete submitData.rental_property_id;
        delete submitData.rental_start_date;
        delete submitData.rental_end_date;
        delete submitData.rental_amount;
        delete submitData.rental_charges;
        delete submitData.rental_currency;
        delete submitData.rental_term_type;
      }

      if (selectedClient) {
        await clientsAPI.update(selectedClient.id, submitData);
        toast.success('Client mis à jour avec succès');
      } else {
        await clientsAPI.create(submitData);
        const successMsg = formData.client_type === 'locataire' && formData.rental_property_id
          ? 'Client locataire créé et bail enregistré avec succès'
          : 'Client ajouté avec succès';
        toast.success(successMsg);
      }
      
      handleCloseForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    
    try {
      await clientsAPI.delete(selectedClient.id);
      toast.success('Client supprimé avec succès');
      setIsDeleteOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadWelcomeLetter = async (client) => {
    try {
      toast.info('Génération du courrier en cours...');
      const response = await clientsAPI.downloadWelcomeLetter(client.id);
      
      // Check if response is an error (JSON instead of PDF)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Erreur lors du téléchargement');
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `courrier_entree_${client.client_code || client.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Courrier téléchargé avec succès');
    } catch (error) {
      // Handle blob response errors
      let message = 'Erreur lors du téléchargement';
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseErr) {
          console.error('Error parsing blob response:', parseErr);
          // Unable to parse blob response
        }
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
    }
  };

  const handleDownloadLeaseContract = async (client) => {
    try {
      toast.info('Génération du contrat de location...');
      
      // Get the tenant's active transaction — strictly scoped to this client
      const transactionsRes = await transactionsAPI.getAll({ 
        client_id: client.id, 
        type: 'location' 
      });
      
      // Defensive filter: even if the backend returned extras, ensure every tx belongs to this client
      const clientTxs = (transactionsRes.data || []).filter(t => t.client_id === client.id);
      const activeTransaction = clientTxs.find(t => t.lease_status === 'actif')
        || clientTxs.sort((a, b) => (b.created_at > a.created_at ? 1 : -1))[0];
      
      if (!activeTransaction) {
        toast.error('Aucun bail actif trouvé pour ce locataire');
        return;
      }
      
      const response = await transactionsAPI.downloadLeaseContract(activeTransaction.id);
      
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Erreur');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat_location_${client.client_code || client.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Contrat de location téléchargé');
    } catch (error) {
      let message = 'Erreur lors du téléchargement du contrat';
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseErr) {
          console.error('Error parsing blob response:', parseErr);
        }
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
    }
  };

  // ==== Mise en demeure + Libération des lieux ====
  const [tenantNoticeCtx, setTenantNoticeCtx] = useState(null);
  const [warnForm, setWarnForm] = useState({ amount_overdue: '', days_overdue: '', grace_period_days: 8 });
  const [evictForm, setEvictForm] = useState({ motif: 'unpaid_rent', motif_detail: '', vacate_deadline_days: 30 });

  const openLateWarning = (client) => {
    setTenantNoticeCtx({ client, type: 'warning' });
    setWarnForm({ amount_overdue: '', days_overdue: '', grace_period_days: 8 });
  };
  const openEvictionNotice = (client) => {
    setTenantNoticeCtx({ client, type: 'eviction' });
    setEvictForm({ motif: 'unpaid_rent', motif_detail: '', vacate_deadline_days: 30 });
  };
  const submitTenantNotice = async () => {
    if (!tenantNoticeCtx) return;
    const { client, type } = tenantNoticeCtx;
    try {
      const url = type === 'warning'
        ? `/tenants/${client.id}/late-warning`
        : `/tenants/${client.id}/eviction-notice`;
      const body = type === 'warning'
        ? {
            amount_overdue: warnForm.amount_overdue ? parseFloat(warnForm.amount_overdue) : null,
            days_overdue: warnForm.days_overdue ? parseInt(warnForm.days_overdue, 10) : null,
            grace_period_days: parseInt(warnForm.grace_period_days || 8, 10),
          }
        : {
            motif: evictForm.motif,
            motif_detail: evictForm.motif_detail || '',
            vacate_deadline_days: parseInt(evictForm.vacate_deadline_days || 30, 10),
          };
      const r = await api.post(url, body, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = type === 'warning'
        ? `mise_en_demeure_${client.name}.pdf`
        : `liberation_lieux_${client.name}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Document téléchargé');
      setTenantNoticeCtx(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadResidenceCertificate = async (client) => {
    try {
      toast.info("Génération de l'attestation de domicile...");
      const response = await api.get(`/tenants/${client.id}/residence-certificate/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_domicile_${client.client_code || client.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attestation de domicile téléchargée');
    } catch (error) {
      let message = "Erreur lors du téléchargement de l'attestation";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          message = JSON.parse(text).detail || message;
        } catch { /* noop */ }
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    }
  };

  const handleOpenSignLease = async (client) => {
    try {
      const transactionsRes = await transactionsAPI.getAll({ client_id: client.id, type: 'location' });
      const clientTxs = (transactionsRes.data || []).filter(t => t.client_id === client.id);
      const activeTransaction = clientTxs.find(t => t.lease_status === 'actif')
        || clientTxs.sort((a, b) => (b.created_at > a.created_at ? 1 : -1))[0];
      if (!activeTransaction) {
        toast.error('Aucun bail actif trouvé pour ce locataire');
        return;
      }
      setSignClientId(client.id);
      setSignTransactionId(activeTransaction.id);
      setShowSignModal(true);
    } catch (error) {
      toast.error('Erreur lors de la récupération du bail');
    }
  };

  const handleSignLeaseContract = async (signatureData) => {
    if (!signTransactionId) return;
    try {
      await signaturesAPI.save({
        document_type: 'lease_contract',
        document_id: signTransactionId,
        signature_data: signatureData
      });
      toast.success('Contrat de location signé avec succès');
      setShowSignModal(false);
      setSignClientId(null);
      setSignTransactionId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  // Fetch pro-rata info whenever the modal opens or the effective end date changes
  useEffect(() => {
    if (!isLeaseTerminateOpen || !clientToTerminate?.id) {
      setLeaseEndProrata(null);
      return;
    }
    let cancelled = false;
    setProrataLoading(true);
    api.get(`/clients/${clientToTerminate.id}/lease-end-prorata`, {
      params: { effective_end_date: leaseEffectiveEndDate },
    })
      .then((r) => { if (!cancelled) setLeaseEndProrata(r.data); })
      .catch(() => { if (!cancelled) setLeaseEndProrata(null); })
      .finally(() => { if (!cancelled) setProrataLoading(false); });
    return () => { cancelled = true; };
  }, [isLeaseTerminateOpen, clientToTerminate?.id, leaseEffectiveEndDate]);

  const handleTerminateLease = async () => {
    if (!clientToTerminate || !leaseTerminationReason) {
      toast.error('Veuillez sélectionner un motif de fin de bail');
      return;
    }
    
    setTerminatingLease(true);
    try {
      const res = await api.put(`/clients/${clientToTerminate.id}/terminate-lease`, {
        end_reason: leaseTerminationReason,
        end_notes: leaseTerminationNotes || null,
        effective_end_date: leaseEffectiveEndDate,
        create_prorata_payment: createProrataPayment,
      });
      const data = res.data || {};
      if (data.prorata_payment_created) {
        toast.success(
          `Bail terminé. Prorata de ${(data.prorata_payment_created.amount || 0).toLocaleString('fr-FR')} FCFA créé (quittance ${data.prorata_payment_created.receipt_number}).`
        );
      } else if (data.refund_needed) {
        toast.warning(
          `Bail terminé. Un remboursement manuel de ${(data.refund_needed.refund_amount || 0).toLocaleString('fr-FR')} FCFA est à effectuer (quittance ${data.refund_needed.receipt_number}).`,
          { duration: 10000 }
        );
      } else {
        toast.success('Bail terminé avec succès');
      }
      setIsLeaseTerminateOpen(false);
      setClientToTerminate(null);
      setLeaseTerminationReason('');
      setLeaseTerminationNotes('');
      setLeaseEndProrata(null);
      // Update lease status locally
      setLeaseStatuses(prev => ({
        ...prev,
        [clientToTerminate.id]: { has_lease: true, lease_status: 'termine' }
      }));
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la terminaison du bail';
      toast.error(message);
    } finally {
      setTerminatingLease(false);
    }
  };

  const handleDownloadLeaseTermination = async (client) => {
    try {
      toast.info('Génération de l\'attestation...');
      const response = await clientsAPI.downloadLeaseTermination(client.id);
      
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Erreur');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_fin_bail_${client.client_code || client.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Attestation de fin de bail téléchargée');
    } catch (error) {
      let message = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseError) {
          console.error('Error parsing blob response:', parseError);
          // Unable to parse blob response
        }
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    }
  };

  const handleReactivateLease = async (client) => {
    try {
      await clientsAPI.reactivateLease(client.id);
      toast.success('Bail réactivé avec succès');
      // Update lease status locally
      setLeaseStatuses(prev => ({
        ...prev,
        [client.id]: { has_lease: true, lease_status: 'actif' }
      }));
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la réactivation du bail';
      toast.error(message);
    }
  };

  // Fetch lease status for tenants
  const fetchLeaseStatus = useCallback(async (clientId) => {
    try {
      const response = await clientsAPI.getLeaseStatus(clientId);
      setLeaseStatuses(prev => ({ ...prev, [clientId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch lease status for client ${clientId}:`, error);
      // Silent fail - lease status check not critical
    }
  }, []);

  // Historical payment functions
  const handleOpenHistoricalPayment = (client) => {
    setClientForPayment(client);
    setHistoricalPaymentData({
      amount: client.rental_amount?.toString() || '',
      currency: client.rental_currency || 'XOF',
      payment_date: new Date(),
      period_month: new Date().getMonth(), // Previous month by default
      period_year: new Date().getFullYear(),
      payment_method: 'especes',
      reference: '',
      notes: '',
      generate_receipt: true
    });
    setIsHistoricalPaymentOpen(true);
  };

  const handleCloseHistoricalPayment = () => {
    setIsHistoricalPaymentOpen(false);
    setClientForPayment(null);
  };

  const handleSubmitHistoricalPayment = async (e) => {
    e.preventDefault();
    
    if (!clientForPayment || !historicalPaymentData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSavingPayment(true);
    try {
      const submitData = {
        property_id: clientForPayment.rental_property_id,
        tenant_id: clientForPayment.id,
        amount: parseFloat(historicalPaymentData.amount),
        currency: historicalPaymentData.currency,
        payment_date: historicalPaymentData.payment_date.toISOString(),
        period_month: parseInt(historicalPaymentData.period_month),
        period_year: parseInt(historicalPaymentData.period_year),
        payment_method: historicalPaymentData.payment_method,
        reference: historicalPaymentData.reference || null,
        notes: historicalPaymentData.notes || null,
        generate_receipt: historicalPaymentData.generate_receipt
      };

      await rentPaymentsAPI.createHistorical(submitData);
      toast.success(
        historicalPaymentData.generate_receipt 
          ? 'Paiement enregistré avec quittance' 
          : 'Paiement enregistré sans quittance'
      );
      
      handleCloseHistoricalPayment();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSavingPayment(false);
    }
  };

  // Fetch lease statuses for all tenants on mount
  useEffect(() => {
    clients.filter(c => c.client_type === 'locataire').forEach(c => {
      fetchLeaseStatus(c.id);
    });
  }, [clients, fetchLeaseStatus]);

  // Identity docs handlers
  const openIdDocs = async (client) => {
    setIdDocsClient(client);
    setIsIdDocsOpen(true);
    setLoadingDocs(true);
    try {
      const res = await clientsAPI.getIdentityDocs(client.id);
      setIdDocs(res.data);
    } catch {
      toast.error('Erreur chargement documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadIdDoc = async (file) => {
    if (!file || !idDocsClient) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', idDocForm.doc_type);
      formData.append('doc_number', idDocForm.doc_number);
      formData.append('date_naissance', idDocForm.date_naissance);
      formData.append('date_debut_validite', idDocForm.date_debut_validite);
      formData.append('date_fin_validite', idDocForm.date_fin_validite);
      formData.append('notes', idDocForm.notes);
      const res = await clientsAPI.uploadIdentityDoc(idDocsClient.id, formData);
      setIdDocs(prev => [res.data.document, ...prev]);
      setIdDocForm({ doc_type: 'carte_identite', doc_number: '', date_naissance: '', date_debut_validite: '', date_fin_validite: '', notes: '' });
      // Stash OCR result for "apply to client" panel
      const ocrFields = res.data.ocr || {};
      const hasOcr = Object.values(ocrFields).some(v => v);
      setOcrResult(hasOcr ? ocrFields : null);
      if (res.data.face_extracted) {
        toast.success('Document ajoute + photo extraite automatiquement !', { duration: 4000 });
        // Update the local client list so the photo shows up immediately
        setClients(prev => prev.map(c =>
          c.id === idDocsClient.id
            ? { ...c, photo_url: res.data.document.extracted_photo_url }
            : c
        ));
        setIdDocsClient(prev => prev ? { ...prev, photo_url: res.data.document.extracted_photo_url } : prev);
        fetchData();
      } else {
        toast.success('Document ajoute');
      }
      if (hasOcr) {
        toast.info('Informations OCR extraites - cliquez "Appliquer" pour mettre a jour la fiche', { duration: 6000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleApplyOcr = async () => {
    if (!ocrResult || !idDocsClient) return;
    try {
      // Build full name from OCR
      const fullName = [ocrResult.first_name, ocrResult.last_name]
        .filter(Boolean).join(' ').trim();
      const payload = {};
      if (fullName) payload.name = fullName;
      // Append OCR notes to existing notes
      const ocrNotes = [
        ocrResult.date_of_birth ? `Ne le: ${ocrResult.date_of_birth}` : null,
        ocrResult.place_of_birth ? `a ${ocrResult.place_of_birth}` : null,
        ocrResult.gender ? `Sexe: ${ocrResult.gender}` : null,
        ocrResult.nationality ? `Nationalite: ${ocrResult.nationality}` : null,
        ocrResult.address ? `Adresse: ${ocrResult.address}` : null,
      ].filter(Boolean).join(' - ');
      if (ocrNotes) {
        const existing = idDocsClient.notes || '';
        payload.notes = existing
          ? `${existing}\n[OCR] ${ocrNotes}`
          : `[OCR] ${ocrNotes}`;
      }
      if (Object.keys(payload).length === 0) {
        toast.info('Aucun champ a mettre a jour');
        return;
      }
      await clientsAPI.update(idDocsClient.id, payload);
      toast.success('Fiche client mise a jour avec les donnees OCR');
      setOcrResult(null);
      // Update local state
      setIdDocsClient((c) => c ? { ...c, ...payload } : c);
      setClients((prev) => prev.map((c) => c.id === idDocsClient.id ? { ...c, ...payload } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
    }
  };

  const handleDeleteIdDoc = async (docId) => {
    if (!idDocsClient) return;
    try {
      await clientsAPI.deleteIdentityDoc(idDocsClient.id, docId);
      setIdDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document supprime');
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handlePrintIdDocs = async () => {
    if (!idDocsClient) return;
    try {
      const response = await clientsAPI.printIdentityDocs(idDocsClient.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fiche_client_${idDocsClient.client_code || idDocsClient.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche client generee');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur generation PDF');
    }
  };

  const filteredClients = clients.filter(client => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = client.name.toLowerCase().includes(searchLower) ||
      client.phone.includes(searchQuery) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      (client.client_code && client.client_code.toLowerCase().includes(searchLower));
    
    const matchesType = filterType === 'all' || client.client_type === filterType;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <ModuleTasksPanel moduleKey="clients" moduleLabel="Clients" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            Clients
            <SyncBadge urlPattern="/clients" />
          </h1>
          <p className="text-gray-500 mt-1">
            {clients.length} client{clients.length > 1 ? 's' : ''} enregistré{clients.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          onClick={() => handleOpenForm()}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un client
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, telephone, email ou code (CLI-...)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-client-type-select">
                <SelectValue placeholder="Type de client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="acheteur">Acheteur</SelectItem>
                <SelectItem value="locataire">Locataire</SelectItem>
                <SelectItem value="vendeur">Vendeur</SelectItem>
                <SelectItem value="bailleur">Bailleur</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      {filteredClients.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Date d'ajout</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {client.photo_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${client.photo_url}`}
                            alt={client.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-[#D4A373]"
                            data-testid={`client-photo-${client.id}`}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#D4A373] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-medium">
                              {client.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{client.name}</p>
                          <p className="text-xs font-mono text-[#1B3A4B] font-semibold">
                            {client.client_code || `CLI-${client.id?.slice(0,6).toUpperCase()}`}
                          </p>
                          {client.client_type === 'locataire'
                            && leaseStatuses[client.id]?.lease_start_date && (
                            <div className="mt-1">
                              <LeaseDurationCounter
                                startDate={leaseStatuses[client.id].lease_start_date}
                                endDate={leaseStatuses[client.id]?.lease_status === 'termine' ? leaseStatuses[client.id]?.lease_end_date : null}
                                variant="compact"
                                accentColor={leaseStatuses[client.id]?.lease_status === 'termine' ? 'rose' : 'amber'}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span>{client.phone}</span>
                        </div>
                        {client.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate max-w-[200px]">{client.email}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={clientTypeColors[client.client_type]}>
                          {clientTypeLabels[client.client_type]}
                        </Badge>
                        {client.client_type === 'locataire' && leaseStatuses[client.id]?.lease_status === 'termine' && (
                          <Badge className="bg-red-100 text-red-800 text-xs">
                            Bail terminé
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.budget_min || client.budget_max ? (
                        <span className="text-sm">
                          {client.budget_min ? formatCurrency(client.budget_min, 'XOF') : '0'} 
                          {' - '}
                          {client.budget_max ? formatCurrency(client.budget_max, 'XOF') : '∞'}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(client.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Welcome Letter button - only for tenants with active lease */}
                        {client.client_type === 'locataire' && leaseStatuses[client.id]?.lease_status !== 'termine' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadWelcomeLetter(client)}
                            title="Télécharger le courrier d'entrée"
                            className="text-[#1B3A4B] hover:text-[#142D3A] hover:bg-[#1B3A4B]/10"
                            data-testid={`download-welcome-letter-${client.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Lease Contract button - only for tenants with active lease */}
                        {client.client_type === 'locataire' && leaseStatuses[client.id]?.has_lease && leaseStatuses[client.id]?.lease_status !== 'termine' && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadLeaseContract(client)}
                              title="Télécharger le contrat de location"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`download-lease-contract-${client.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <RemoteSignButton compact docType="lease" docId={client.id} />
                            <AgencySignButton docType="lease" docId={client.id} />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openLateWarning(client)}
                              title="Mise en demeure — retard de paiement"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              data-testid={`warn-late-${client.id}`}
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEvictionNotice(client)}
                              title="Demande de libération des lieux"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              data-testid={`evict-notice-${client.id}`}
                            >
                              <LogOut className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadResidenceCertificate(client)}
                              title="Télécharger l'attestation de domicile"
                              className="text-[#D4A745] hover:text-[#B8902F] hover:bg-[#D4A745]/10"
                              data-testid={`download-residence-cert-${client.id}`}
                            >
                              <Home className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setAttestationsHistoryClient(client)}
                              title="Historique et révocation des attestations"
                              className="text-gray-600 hover:text-[#1B3A4B] hover:bg-gray-100"
                              data-testid={`attestations-history-${client.id}`}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenSignLease(client)}
                              title="Signer le contrat de location"
                              className="text-[#1B3A4B] hover:text-[#142D3A] hover:bg-[#1B3A4B]/10"
                              data-testid={`sign-lease-contract-${client.id}`}
                            >
                              <PenLine className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {/* Historical Payment button - only for tenants with active lease */}
                        {client.client_type === 'locataire' && leaseStatuses[client.id]?.has_lease && leaseStatuses[client.id]?.lease_status !== 'termine' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              // Get property info from lease status
                              const leaseData = leaseStatuses[client.id];
                              const clientWithPropertyInfo = {
                                ...client,
                                rental_property_id: leaseData?.property_id,
                                rental_property_title: leaseData?.property_title,
                                rental_amount: leaseData?.rent_amount || client.budget,
                                rental_currency: leaseData?.currency || 'XOF'
                              };
                              handleOpenHistoricalPayment(clientWithPropertyInfo);
                            }}
                            title="Ajouter un ancien paiement"
                            className="text-[#D4A373] hover:text-[#c4935f] hover:bg-[#D4A373]/10"
                            data-testid={`add-historical-payment-${client.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Lease actions - only for tenants */}
                        {client.client_type === 'locataire' && leaseStatuses[client.id]?.has_lease && (
                          <>
                            {leaseStatuses[client.id]?.lease_status !== 'termine' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setClientToTerminate(client);
                                  setIsLeaseTerminateOpen(true);
                                }}
                                title="Mettre fin au bail"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                data-testid={`terminate-lease-${client.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadLeaseTermination(client)}
                                  title="Télécharger l'attestation de fin de bail"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`download-lease-termination-${client.id}`}
                                >
                                  <FileCheck className="w-4 h-4" />
                                </Button>
                                <RemoteSignButton compact docType="lease_termination" docId={client.id} />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleReactivateLease(client)}
                                  title="Réactiver le bail"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`reactivate-lease-${client.id}`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openIdDocs(client)}
                          title="Pieces justificatives"
                          data-testid={`id-docs-${client.id}`}
                        >
                          <CreditCard className="w-4 h-4 text-purple-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenForm(client)}
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedClient(client);
                              setIsDeleteOpen(true);
                            }}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun client trouvé</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || filterType !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Commencez par ajouter votre premier client'}
              </p>
              {!searchQuery && filterType === 'all' && (
                <Button 
                  onClick={() => handleOpenForm()}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un client
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient ? 'Modifier le client' : 'Ajouter un client'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Tabs defaultValue={selectedClient ? 'summary' : 'identity'} className="space-y-4">
              <TabsList className="flex flex-wrap h-auto gap-1 justify-start">
                {selectedClient && (
                  <TabsTrigger value="summary" data-testid="tab-client-summary">Vue 360</TabsTrigger>
                )}
                <TabsTrigger value="identity" data-testid="tab-client-identity">Identite</TabsTrigger>
                <TabsTrigger value="contact" data-testid="tab-client-contact">Coordonnees</TabsTrigger>
                <TabsTrigger value="profession" data-testid="tab-client-profession">Profession</TabsTrigger>
                <TabsTrigger value="emergency" data-testid="tab-client-emergency">Urgence & Garant</TabsTrigger>
                <TabsTrigger value="bank" data-testid="tab-client-bank">Bancaire</TabsTrigger>
                {!selectedClient && formData.client_type === 'locataire' && (
                  <TabsTrigger value="rental" data-testid="tab-client-rental">Bail</TabsTrigger>
                )}
                {selectedClient && formData.client_type === 'proprietaire' && (
                  <TabsTrigger value="keys" data-testid="tab-client-keys">Stock de cles</TabsTrigger>
                )}
                <TabsTrigger value="notes" data-testid="tab-client-notes">Notes</TabsTrigger>
              </TabsList>

              {/* TAB VUE 360 */}
              {selectedClient && (
                <TabsContent value="summary" className="space-y-4">
                  {loadingSummary ? (
                    <div className="py-8 text-center text-gray-500 text-sm">Chargement de la vue d'ensemble...</div>
                  ) : !clientSummary ? (
                    <div className="py-8 text-center text-gray-500 text-sm">Aucune donnee disponible.</div>
                  ) : (
                    <div className="space-y-4" data-testid="client-360-summary">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3">
                          <p className="text-[11px] text-emerald-700 uppercase tracking-wide">Total paye</p>
                          <p className="text-lg font-bold text-emerald-900">
                            {formatCurrency(clientSummary.totals.total_paid || 0, 'XOF')}
                          </p>
                        </div>
                        <div className="rounded-lg border bg-blue-50 border-blue-200 p-3">
                          <p className="text-[11px] text-blue-700 uppercase tracking-wide">Quittances</p>
                          <p className="text-lg font-bold text-blue-900">{clientSummary.totals.receipts_count}</p>
                        </div>
                        <div className="rounded-lg border bg-purple-50 border-purple-200 p-3">
                          <p className="text-[11px] text-purple-700 uppercase tracking-wide">Depots garantie</p>
                          <p className="text-lg font-bold text-purple-900">
                            {formatCurrency(clientSummary.totals.deposits_total || 0, 'XOF')}
                          </p>
                          <p className="text-[10px] text-purple-700">{clientSummary.totals.deposits_count} depot(s)</p>
                        </div>
                        <div className="rounded-lg border bg-orange-50 border-orange-200 p-3">
                          <p className="text-[11px] text-orange-700 uppercase tracking-wide">Pieces ID</p>
                          <p className="text-lg font-bold text-orange-900">{clientSummary.totals.id_docs_count}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="rounded-lg border bg-gray-50 p-3">
                          <p className="text-[11px] text-gray-600 uppercase tracking-wide">Transactions</p>
                          <p className="text-lg font-bold text-gray-900">{clientSummary.totals.transactions_count}</p>
                        </div>
                        <div className="rounded-lg border bg-gray-50 p-3">
                          <p className="text-[11px] text-gray-600 uppercase tracking-wide">Demandes</p>
                          <p className="text-lg font-bold text-gray-900">{clientSummary.totals.total_requests}</p>
                          {clientSummary.totals.pending_requests > 0 && (
                            <p className="text-[10px] text-blue-600">{clientSummary.totals.pending_requests} en attente</p>
                          )}
                        </div>
                        <div className="rounded-lg border bg-gray-50 p-3">
                          <p className="text-[11px] text-gray-600 uppercase tracking-wide">Anomalies</p>
                          <p className="text-lg font-bold text-gray-900">{clientSummary.totals.anomalies_count}</p>
                          {clientSummary.totals.open_anomalies > 0 && (
                            <p className="text-[10px] text-red-600">{clientSummary.totals.open_anomalies} ouvertes</p>
                          )}
                        </div>
                        <div className="rounded-lg border bg-gray-50 p-3">
                          <p className="text-[11px] text-gray-600 uppercase tracking-wide">Dernier paiement</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {clientSummary.totals.last_payment
                              ? format(new Date(clientSummary.totals.last_payment), 'dd MMM yyyy', { locale: fr })
                              : '-'}
                          </p>
                        </div>
                      </div>

                      {clientSummary.active_rental && (
                        <div className="rounded-lg border-2 border-[#D4A745]/40 bg-[#D4A745]/5 p-4">
                          <p className="text-xs text-[#1B3A4B] font-semibold uppercase tracking-wide mb-2">Bail actif</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                            {clientSummary.active_rental.rent_amount != null && (
                              <div>
                                <p className="text-xs text-gray-500">Loyer mensuel</p>
                                <p className="font-semibold">{formatCurrency(clientSummary.active_rental.rent_amount, 'XOF')}</p>
                              </div>
                            )}
                            {clientSummary.active_rental.lease_start_date && (
                              <div>
                                <p className="text-xs text-gray-500">Debut</p>
                                <p className="font-semibold">{format(new Date(clientSummary.active_rental.lease_start_date), 'dd/MM/yyyy', { locale: fr })}</p>
                              </div>
                            )}
                            {clientSummary.active_rental.lease_end_date && (
                              <div>
                                <p className="text-xs text-gray-500">Fin</p>
                                <p className="font-semibold">{format(new Date(clientSummary.active_rental.lease_end_date), 'dd/MM/yyyy', { locale: fr })}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {clientSummary.recent_transactions?.length > 0 && (
                        <div className="rounded-lg border bg-white p-4">
                          <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-3">5 dernieres transactions</p>
                          <div className="space-y-2">
                            {clientSummary.recent_transactions.slice(0, 5).map((t) => (
                              <div key={t.id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                                <div>
                                  <p className="font-medium text-gray-900 capitalize">{t.transaction_type}</p>
                                  <p className="text-xs text-gray-500">
                                    {t.transaction_date ? format(new Date(t.transaction_date), 'dd MMM yyyy', { locale: fr }) : ''}
                                    {t.unit_name ? ` - ${t.unit_name}` : ''}
                                  </p>
                                </div>
                                <p className="font-semibold text-[#1B3A4B]">{formatCurrency(t.amount || 0, t.currency || 'XOF')}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              )}

              {/* TAB IDENTITE */}
              <TabsContent value="identity" className="space-y-4">
                {/* --- Scan de pièce d'identité (OCR) - visible uniquement en création --- */}
                {!selectedClient && (
                  <div className="rounded-xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-4" data-testid="scan-id-section">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow">
                        <ScanLine className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-bold text-amber-900">Scanner la pièce d&apos;identité</h4>
                        <p className="text-xs text-amber-800 mt-0.5">
                          Photographiez ou téléversez une CNI, NINA, passeport… Les champs sont pré-remplis automatiquement.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <input
                            ref={scanInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handleScanId(e.target.files[0])}
                            data-testid="scan-id-input"
                          />
                          <input
                            ref={scanCameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handleScanId(e.target.files[0])}
                            data-testid="scan-id-camera-input"
                          />
                          <Button
                            type="button"
                            onClick={() => scanCameraInputRef.current?.click()}
                            disabled={scanning}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="scan-id-camera-btn"
                          >
                            {scanning ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours…</>
                            ) : (
                              <><Camera className="w-4 h-4 mr-2" /> Prendre en photo</>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => scanInputRef.current?.click()}
                            disabled={scanning}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="scan-id-btn"
                          >
                            {scanFile ? (
                              <><ScanLine className="w-4 h-4 mr-2" /> Rescanner (fichier)</>
                            ) : (
                              <><ScanLine className="w-4 h-4 mr-2" /> Choisir un fichier</>
                            )}
                          </Button>
                          {scanFile && !scanning && (
                            <Button type="button" variant="outline" size="sm" onClick={resetScan} data-testid="scan-id-reset">
                              <XCircle className="w-4 h-4 mr-1" /> Retirer
                            </Button>
                          )}
                        </div>
                        {scanPreview && (
                          <div className="mt-3 flex items-start gap-3">
                            <img
                              src={scanPreview}
                              alt="Aperçu pièce d'identité"
                              className="w-24 h-16 object-cover rounded border border-amber-200"
                            />
                            {scannedFields && (
                              <div className="text-[11px] text-gray-700 space-y-0.5">
                                {scannedFields.id_doc_type && <div>Type&nbsp;: <b>{scannedFields.id_doc_type}</b></div>}
                                {scannedFields.id_doc_number && <div>N°&nbsp;: <b>{scannedFields.id_doc_number}</b></div>}
                                {scannedFields.id_expiry_date && <div>Expire&nbsp;: <b>{scannedFields.id_expiry_date}</b></div>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Amadou Diallo"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_code">Identifiant personnalise</Label>
                    <Input
                      id="client_code"
                      value={formData.client_code}
                      onChange={(e) => setFormData({ ...formData, client_code: e.target.value })}
                      placeholder={selectedClient ? selectedClient.client_code : "Ex: CLI-001"}
                      data-testid="client-code-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Vide = auto</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="client_type">Type de client *</Label>
                  <Select
                    value={formData.client_type}
                    onValueChange={(value) => setFormData({ ...formData, client_type: value })}
                  >
                    <SelectTrigger data-testid="client-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="acheteur">Acheteur</SelectItem>
                      <SelectItem value="locataire">Locataire</SelectItem>
                      <SelectItem value="vendeur">Vendeur</SelectItem>
                      <SelectItem value="bailleur">Bailleur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="birth_date">Date de naissance</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      data-testid="client-birth-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="birth_place">Lieu de naissance</Label>
                    <Input
                      id="birth_place"
                      value={formData.birth_place}
                      onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                      placeholder="Bamako, Mali"
                      data-testid="client-birth-place-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="nationality">Nationalite</Label>
                    <Input
                      id="nationality"
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      placeholder="Malienne"
                      data-testid="client-nationality-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Sexe</Label>
                    <Select
                      value={formData.gender || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, gender: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger data-testid="client-gender-select">
                        <SelectValue placeholder="Selectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-</SelectItem>
                        <SelectItem value="M">Masculin (M)</SelectItem>
                        <SelectItem value="F">Feminin (F)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="marital_status">Situation matrimoniale</Label>
                    <Select
                      value={formData.marital_status || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, marital_status: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger data-testid="client-marital-select">
                        <SelectValue placeholder="Selectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non renseigne</SelectItem>
                        <SelectItem value="celibataire">Celibataire</SelectItem>
                        <SelectItem value="marie">Marie(e)</SelectItem>
                        <SelectItem value="divorce">Divorce(e)</SelectItem>
                        <SelectItem value="veuf">Veuf(ve)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* TAB COORDONNEES */}
              <TabsContent value="contact" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Telephone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+223 70 00 00 00"
                      data-testid="client-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="amadou@email.com"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Adresse complete</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Rue, quartier, ville, pays..."
                    rows={2}
                    data-testid="client-address-input"
                  />
                </div>
              </TabsContent>

              {/* TAB PROFESSION */}
              <TabsContent value="profession" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profession">Profession</Label>
                    <Input
                      id="profession"
                      value={formData.profession}
                      onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                      placeholder="Ingenieur, Commercant, Fonctionnaire..."
                      data-testid="client-profession-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="employer">Employeur / Entreprise</Label>
                    <Input
                      id="employer"
                      value={formData.employer}
                      onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                      placeholder="Nom de la structure"
                      data-testid="client-employer-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="monthly_income">Revenu mensuel net (FCFA)</Label>
                  <Input
                    id="monthly_income"
                    type="number"
                    value={formData.monthly_income}
                    onChange={(e) => setFormData({ ...formData, monthly_income: e.target.value })}
                    placeholder="350000"
                    data-testid="client-income-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget_min">Budget min (FCFA)</Label>
                    <Input
                      id="budget_min"
                      type="number"
                      value={formData.budget_min}
                      onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                      placeholder="10000000"
                      data-testid="client-budget-min-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget_max">Budget max (FCFA)</Label>
                    <Input
                      id="budget_max"
                      type="number"
                      value={formData.budget_max}
                      onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                      placeholder="50000000"
                      data-testid="client-budget-max-input"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* TAB URGENCE & GARANT */}
              <TabsContent value="emergency" className="space-y-5">
                <div className="border rounded-lg p-4 space-y-3 bg-rose-50/40 border-rose-100">
                  <h4 className="font-semibold text-rose-900 text-sm">Contact d'urgence</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ec_name">Nom & prenom</Label>
                      <Input id="ec_name" value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        placeholder="Aminata Coulibaly"
                        data-testid="client-ec-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="ec_phone">Telephone</Label>
                      <Input id="ec_phone" value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        placeholder="+223 70 00 00 00"
                        data-testid="client-ec-phone-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ec_relation">Lien de parente</Label>
                    <Input id="ec_relation" value={formData.emergency_contact_relation}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })}
                      placeholder="Conjoint, Parent, Frere..."
                      data-testid="client-ec-relation-input" />
                  </div>
                </div>

                <div className="border rounded-lg p-4 space-y-3 bg-emerald-50/40 border-emerald-100">
                  <h4 className="font-semibold text-emerald-900 text-sm">Garant (caution)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="g_name">Nom & prenom</Label>
                      <Input id="g_name" value={formData.guarantor_name}
                        onChange={(e) => setFormData({ ...formData, guarantor_name: e.target.value })}
                        placeholder="Mamadou Traore"
                        data-testid="client-guarantor-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="g_phone">Telephone</Label>
                      <Input id="g_phone" value={formData.guarantor_phone}
                        onChange={(e) => setFormData({ ...formData, guarantor_phone: e.target.value })}
                        placeholder="+223 75 00 00 00"
                        data-testid="client-guarantor-phone-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="g_profession">Profession</Label>
                      <Input id="g_profession" value={formData.guarantor_profession}
                        onChange={(e) => setFormData({ ...formData, guarantor_profession: e.target.value })}
                        placeholder="Cadre, Fonctionnaire..."
                        data-testid="client-guarantor-profession-input" />
                    </div>
                    <div>
                      <Label htmlFor="g_address">Adresse</Label>
                      <Input id="g_address" value={formData.guarantor_address}
                        onChange={(e) => setFormData({ ...formData, guarantor_address: e.target.value })}
                        placeholder="Quartier, ville"
                        data-testid="client-guarantor-address-input" />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB BANCAIRE */}
              <TabsContent value="bank" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bank_name">Banque</Label>
                    <Input id="bank_name" value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="BDM, Orabank, BIM..."
                      data-testid="client-bank-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="bank_account">Numero de compte</Label>
                    <Input id="bank_account" value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      placeholder="123456789012"
                      data-testid="client-bank-account-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bank_iban">IBAN / RIB complet</Label>
                  <Input id="bank_iban" value={formData.bank_iban}
                    onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                    placeholder="ML00 0000 0000 0000 0000 0000 0000"
                    data-testid="client-bank-iban-input" />
                </div>
              </TabsContent>

              {/* TAB BAIL (locataire only, new only) */}
              {!selectedClient && formData.client_type === 'locataire' && (
                <TabsContent value="rental" className="space-y-4">
                  <div className="border border-[#D4A373]/30 rounded-lg p-4 bg-[#D4A373]/5 space-y-4">
                    <div className="flex items-center gap-2">
                      <Home className="w-5 h-5 text-[#1B3A4B]" />
                      <h4 className="font-medium text-[#1B3A4B]">Attribution du bien en location</h4>
                    </div>
                    <p className="text-xs text-gray-500">
                      Optionnel : Assignez directement un bien a ce locataire lors de la creation
                    </p>

                    <div>
                      <Label htmlFor="rental_property_id">Bien a louer</Label>
                      <Select
                        value={formData.rental_property_id || 'none'}
                        onValueChange={(value) => {
                          const actualValue = value === 'none' ? '' : value;
                          const property = rentalProperties.find(p => p.id === actualValue);
                          setFormData({
                            ...formData,
                            rental_property_id: actualValue,
                            rental_amount: property?.price?.toString() || formData.rental_amount,
                            rental_currency: property?.currency || formData.rental_currency
                          });
                        }}
                      >
                        <SelectTrigger data-testid="rental-property-select">
                          <SelectValue placeholder="Selectionner un bien disponible" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun (attribuer plus tard)</SelectItem>
                          {rentalProperties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <span>{property.title} - {property.district}</span>
                                <span className="text-gray-500">({formatCurrency(property.price, property.currency)}/mois)</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {rentalProperties.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Aucun bien en location disponible. Vous pourrez attribuer le bien plus tard.
                        </p>
                      )}
                    </div>

                    {formData.rental_property_id && (
                      <>
                        <div>
                          <Label>Date de debut du bail *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !formData.rental_start_date && "text-muted-foreground"
                                )}
                                data-testid="rental-start-date-btn"
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {formData.rental_start_date
                                  ? format(formData.rental_start_date, "PPP", { locale: fr })
                                  : "Selectionner une date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <CalendarComponent
                                mode="single"
                                selected={formData.rental_start_date}
                                onSelect={(date) => setFormData({ ...formData, rental_start_date: date || new Date() })}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div>
                          <Label>Date de fin du bail (optionnel)</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !formData.rental_end_date && "text-muted-foreground"
                                )}
                                data-testid="rental-end-date-btn"
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {formData.rental_end_date
                                  ? format(formData.rental_end_date, "PPP", { locale: fr })
                                  : "Bail a duree indeterminee"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <CalendarComponent
                                mode="single"
                                selected={formData.rental_end_date}
                                onSelect={(date) => setFormData({ ...formData, rental_end_date: date || null })}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          {formData.rental_end_date && (
                            <Button variant="link" size="sm" className="text-xs text-gray-500 p-0 h-auto mt-1"
                              onClick={() => setFormData({ ...formData, rental_end_date: null })}>
                              Remettre en duree indeterminee
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="rental_amount">Loyer mensuel *</Label>
                            <Input
                              id="rental_amount"
                              type="number"
                              value={formData.rental_amount}
                              onChange={(e) => setFormData({ ...formData, rental_amount: e.target.value })}
                              placeholder="150000"
                              data-testid="rental-amount-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="rental_charges">Charges mensuelles</Label>
                            <Input
                              id="rental_charges"
                              type="number"
                              value={formData.rental_charges}
                              onChange={(e) => setFormData({ ...formData, rental_charges: e.target.value })}
                              placeholder="10000"
                              data-testid="rental-charges-input"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="rental_currency">Devise</Label>
                          <Select
                            value={formData.rental_currency}
                            onValueChange={(value) => setFormData({ ...formData, rental_currency: value })}
                          >
                            <SelectTrigger data-testid="rental-currency-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                              <SelectItem value="EUR">Euro (EUR)</SelectItem>
                              <SelectItem value="USD">Dollar (USD)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="rental_term_type">Type de terme *</Label>
                          <Select
                            value={formData.rental_term_type}
                            onValueChange={(value) => setFormData({ ...formData, rental_term_type: value })}
                          >
                            <SelectTrigger data-testid="rental-term-type-select">
                              <SelectValue placeholder="Selectionner le type de terme" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="terme_a_echoir">
                                <div className="flex flex-col">
                                  <span className="font-medium">Terme a echoir</span>
                                  <span className="text-xs text-gray-500">Paiement en debut de periode (le plus courant)</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="terme_echu">
                                <div className="flex flex-col">
                                  <span className="font-medium">Terme echu</span>
                                  <span className="text-xs text-gray-500">Paiement en fin de periode</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {formData.rental_amount && (
                          <div className="bg-[#1B3A4B]/10 rounded p-3 text-sm">
                            <p className="font-medium text-[#1B3A4B]">Recapitulatif du bail :</p>
                            <ul className="mt-1 space-y-1 text-gray-600">
                              <li>Loyer : {formatCurrency(parseFloat(formData.rental_amount) || 0, formData.rental_currency)}/mois</li>
                              {formData.rental_charges && (
                                <li>Charges : {formatCurrency(parseFloat(formData.rental_charges) || 0, formData.rental_currency)}/mois</li>
                              )}
                              <li className="font-semibold text-[#1B3A4B]">
                                Total mensuel : {formatCurrency(
                                  (parseFloat(formData.rental_amount) || 0) + (parseFloat(formData.rental_charges) || 0),
                                  formData.rental_currency
                                )}
                              </li>
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* TAB STOCK DE CLES (proprietaire uniquement, en edition) */}
              {selectedClient && formData.client_type === 'proprietaire' && (
                <TabsContent value="keys" className="space-y-4">
                  <KeysInventoryTab clientId={selectedClient.id} />
                </TabsContent>
              )}

              {/* TAB NOTES */}
              <TabsContent value="notes" className="space-y-4">
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes sur le client..."
                    rows={6}
                    data-testid="client-notes-input"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="client-submit-btn"
              >
                {saving ? 'Enregistrement...' : selectedClient ? 'Mettre a jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le client "{selectedClient?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lease Termination Dialog */}
      <Dialog open={isLeaseTerminateOpen} onOpenChange={setIsLeaseTerminateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Mettre fin au bail
            </DialogTitle>
          </DialogHeader>
          
          {clientToTerminate && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Locataire concerné:</p>
                <p className="font-semibold">{clientToTerminate.name}</p>
                <p className="text-sm text-gray-500">{clientToTerminate.client_code}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lease-effective-end-date">Date effective de fin de bail *</Label>
                <Input
                  id="lease-effective-end-date"
                  type="date"
                  value={leaseEffectiveEndDate}
                  onChange={(e) => setLeaseEffectiveEndDate(e.target.value)}
                  data-testid="lease-effective-end-date-input"
                />
              </div>

              {/* Prorata info */}
              {prorataLoading && (
                <div className="text-xs text-gray-500 italic">Calcul du prorata en cours…</div>
              )}
              {leaseEndProrata?.prorata && (
                <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3" data-testid="prorata-info-banner">
                  <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-1">
                    Prorata du dernier mois
                  </div>
                  <div className="text-sm text-amber-900">
                    <b>{leaseEndProrata.prorata.days} jour(s) / 30</b> sur un loyer mensuel de{' '}
                    <b>{(leaseEndProrata.prorata.monthly_rent || 0).toLocaleString('fr-FR')} FCFA</b>
                    {leaseEndProrata.prorata.monthly_charges > 0 && (
                      <> + charges <b>{leaseEndProrata.prorata.monthly_charges.toLocaleString('fr-FR')} FCFA</b></>
                    )}
                  </div>
                  <div className="text-2xl font-bold text-amber-800 mt-1" data-testid="prorata-amount">
                    = {(leaseEndProrata.prorata.amount || 0).toLocaleString('fr-FR')} FCFA
                  </div>
                  {!leaseEndProrata.existing_payment?.exists ? (
                    <label className="flex items-center gap-2 mt-3 text-xs text-amber-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createProrataPayment}
                        onChange={(e) => setCreateProrataPayment(e.target.checked)}
                        data-testid="create-prorata-payment-checkbox"
                        className="accent-amber-600"
                      />
                      Générer automatiquement un paiement <b>&quot;prorata_sortie&quot;</b> (visible sur le relevé propriétaire)
                    </label>
                  ) : leaseEndProrata.existing_payment.refund_amount > 0 ? (
                    <div className="mt-3 p-2 rounded-md bg-red-100 border border-red-200 text-xs text-red-900" data-testid="refund-warning">
                      <b>Attention :</b> un paiement de{' '}
                      <b>{leaseEndProrata.existing_payment.amount.toLocaleString('fr-FR')} FCFA</b> a déjà été
                      encaissé pour ce mois (quittance {leaseEndProrata.existing_payment.receipt_number}).<br />
                      Un remboursement manuel de{' '}
                      <b>{leaseEndProrata.existing_payment.refund_amount.toLocaleString('fr-FR')} FCFA</b>{' '}
                      devra être effectué au locataire.
                    </div>
                  ) : (
                    <div className="mt-3 p-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900">
                      Un paiement existe déjà pour le mois de fin, aucune ligne supplémentaire ne sera créée.
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="lease-termination-reason">Motif de fin de bail *</Label>
                <Select
                  value={leaseTerminationReason}
                  onValueChange={setLeaseTerminationReason}
                >
                  <SelectTrigger data-testid="lease-termination-reason-select">
                    <SelectValue placeholder="Sélectionnez un motif" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fin_normale">Fin normale du contrat</SelectItem>
                    <SelectItem value="resiliation_locataire">Résiliation par le locataire</SelectItem>
                    <SelectItem value="resiliation_bailleur">Résiliation par le bailleur</SelectItem>
                    <SelectItem value="expulsion">Expulsion</SelectItem>
                    <SelectItem value="deces">Décès du locataire</SelectItem>
                    <SelectItem value="autre">Autre motif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lease-termination-notes">Observations (optionnel)</Label>
                <Textarea
                  id="lease-termination-notes"
                  value={leaseTerminationNotes}
                  onChange={(e) => setLeaseTerminationNotes(e.target.value)}
                  placeholder="Informations complémentaires..."
                  rows={3}
                  data-testid="lease-termination-notes-input"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Attention:</strong> Cette action va libérer le bien et supprimer l'accès locataire associé. 
                  Vous pourrez télécharger une attestation de fin de bail après confirmation.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsLeaseTerminateOpen(false);
                setClientToTerminate(null);
                setLeaseTerminationReason('');
                setLeaseTerminationNotes('');
                setLeaseEndProrata(null);
                setLeaseEffectiveEndDate(new Date().toISOString().slice(0, 10));
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleTerminateLease}
              className="bg-red-600 hover:bg-red-700"
              disabled={terminatingLease || !leaseTerminationReason}
              data-testid="confirm-terminate-lease-btn"
            >
              {terminatingLease ? 'Traitement...' : 'Mettre fin au bail'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historical Payment Modal */}
      <Dialog open={isHistoricalPaymentOpen} onOpenChange={setIsHistoricalPaymentOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#D4A373]" />
              Ancien paiement - {clientForPayment?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitHistoricalPayment} className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Bien:</strong> {clientForPayment?.rental_property_title || 'N/A'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Loyer mensuel:</strong> {formatCurrency(clientForPayment?.rental_amount, clientForPayment?.rental_currency || 'XOF')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pay_period_month">Mois *</Label>
                <Select
                  value={historicalPaymentData.period_month.toString()}
                  onValueChange={(value) => setHistoricalPaymentData({ ...historicalPaymentData, period_month: parseInt(value) })}
                >
                  <SelectTrigger data-testid="client-hist-month-select">
                    <SelectValue placeholder="Mois" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Janvier</SelectItem>
                    <SelectItem value="2">Février</SelectItem>
                    <SelectItem value="3">Mars</SelectItem>
                    <SelectItem value="4">Avril</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Juin</SelectItem>
                    <SelectItem value="7">Juillet</SelectItem>
                    <SelectItem value="8">Août</SelectItem>
                    <SelectItem value="9">Septembre</SelectItem>
                    <SelectItem value="10">Octobre</SelectItem>
                    <SelectItem value="11">Novembre</SelectItem>
                    <SelectItem value="12">Décembre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pay_period_year">Année *</Label>
                <Select
                  value={historicalPaymentData.period_year.toString()}
                  onValueChange={(value) => setHistoricalPaymentData({ ...historicalPaymentData, period_year: parseInt(value) })}
                >
                  <SelectTrigger data-testid="client-hist-year-select">
                    <SelectValue placeholder="Année" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pay_amount">Montant payé *</Label>
                <Input
                  id="pay_amount"
                  type="number"
                  value={historicalPaymentData.amount}
                  onChange={(e) => setHistoricalPaymentData({ ...historicalPaymentData, amount: e.target.value })}
                  placeholder="0"
                  data-testid="client-hist-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="pay_currency">Devise</Label>
                <Select
                  value={historicalPaymentData.currency}
                  onValueChange={(value) => setHistoricalPaymentData({ ...historicalPaymentData, currency: value })}
                >
                  <SelectTrigger data-testid="client-hist-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                    <SelectItem value="EUR">Euro (EUR)</SelectItem>
                    <SelectItem value="USD">Dollar (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="pay_payment_date">Date du paiement</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !historicalPaymentData.payment_date && "text-muted-foreground"
                    )}
                    data-testid="client-hist-date-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {historicalPaymentData.payment_date 
                      ? format(historicalPaymentData.payment_date, 'PPP', { locale: fr })
                      : "Sélectionner une date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={historicalPaymentData.payment_date}
                    onSelect={(date) => setHistoricalPaymentData({ ...historicalPaymentData, payment_date: date || new Date() })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="pay_payment_method">Mode de paiement</Label>
              <Select
                value={historicalPaymentData.payment_method}
                onValueChange={(value) => setHistoricalPaymentData({ ...historicalPaymentData, payment_method: value })}
              >
                <SelectTrigger data-testid="client-hist-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="pay_notes">Notes (optionnel)</Label>
              <Input
                id="pay_notes"
                value={historicalPaymentData.notes}
                onChange={(e) => setHistoricalPaymentData({ ...historicalPaymentData, notes: e.target.value })}
                placeholder="Notes ou commentaires..."
                data-testid="client-hist-notes-input"
              />
            </div>

            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="pay_generate_receipt"
                checked={historicalPaymentData.generate_receipt}
                onChange={(e) => setHistoricalPaymentData({ ...historicalPaymentData, generate_receipt: e.target.checked })}
                className="h-4 w-4 text-[#1B3A4B] border-gray-300 rounded focus:ring-[#1B3A4B]"
                data-testid="client-hist-receipt-checkbox"
              />
              <Label htmlFor="pay_generate_receipt" className="text-sm font-medium cursor-pointer">
                Générer une quittance pour ce paiement
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseHistoricalPayment}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={savingPayment}
                data-testid="submit-client-hist-payment-btn"
              >
                {savingPayment ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <History className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Signature Modal for Lease Contract */}
      <Dialog open={showSignModal} onOpenChange={(open) => { setShowSignModal(open); if (!open) { setSignClientId(null); setSignTransactionId(null); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#1B3A4B]" />
              Signer le Contrat de Location
            </DialogTitle>
          </DialogHeader>
          <SignaturePad
            onSave={handleSignLeaseContract}
            onCancel={() => { setShowSignModal(false); setSignClientId(null); setSignTransactionId(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Identity Documents Modal */}
      <Dialog open={isIdDocsOpen} onOpenChange={(open) => { setIsIdDocsOpen(open); if (!open) { setIdDocsClient(null); setIdDocs([]); setIdDocPreview(null); } }}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Pieces justificatives - {idDocsClient?.name}</DialogTitle>
              {idDocs.length > 0 && (
                <Button variant="outline" size="sm" onClick={handlePrintIdDocs}
                  className="text-[#1B3A4B] border-[#1B3A4B] hover:bg-[#1B3A4B] hover:text-white"
                  data-testid="print-id-docs">
                  <Printer className="w-4 h-4 mr-1" /> Imprimer fiche
                </Button>
              )}
            </div>
          </DialogHeader>

          {/* Extracted portrait from ID */}
          {idDocsClient?.photo_url && (
            <div className="flex items-center gap-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${idDocsClient.photo_url}`}
                alt="Portrait"
                className="w-16 h-20 rounded-md object-cover ring-2 ring-emerald-300"
                data-testid="client-extracted-photo"
              />
              <div className="text-sm">
                <p className="font-medium text-emerald-800">Photo extraite automatiquement</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  La photo a ete detectee sur la piece d'identite et associee au client.
                </p>
              </div>
            </div>
          )}

          {/* OCR results panel */}
          {ocrResult && (
            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg space-y-3" data-testid="ocr-result-panel">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-blue-900 flex items-center gap-2">
                  <span className="text-lg">✨</span>
                  Informations detectees par IA
                </p>
                <Button size="sm" variant="ghost" onClick={() => setOcrResult(null)} className="text-blue-700">
                  Ignorer
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs bg-white rounded p-3">
                {ocrResult.last_name && <div><span className="text-gray-500">Nom :</span> <span className="font-mono font-semibold">{ocrResult.last_name}</span></div>}
                {ocrResult.first_name && <div><span className="text-gray-500">Prenom :</span> <span className="font-mono font-semibold">{ocrResult.first_name}</span></div>}
                {ocrResult.date_of_birth && <div><span className="text-gray-500">Naissance :</span> <span className="font-mono">{ocrResult.date_of_birth}</span></div>}
                {ocrResult.place_of_birth && <div><span className="text-gray-500">Lieu :</span> <span className="font-mono">{ocrResult.place_of_birth}</span></div>}
                {ocrResult.gender && <div><span className="text-gray-500">Sexe :</span> <span className="font-mono">{ocrResult.gender}</span></div>}
                {ocrResult.nationality && <div><span className="text-gray-500">Nationalite :</span> <span className="font-mono">{ocrResult.nationality}</span></div>}
                {ocrResult.doc_number && <div><span className="text-gray-500">N° Doc :</span> <span className="font-mono">{ocrResult.doc_number}</span></div>}
                {ocrResult.expiry_date && <div><span className="text-gray-500">Expire le :</span> <span className="font-mono">{ocrResult.expiry_date}</span></div>}
                {ocrResult.address && <div className="col-span-2"><span className="text-gray-500">Adresse :</span> <span className="font-mono">{ocrResult.address}</span></div>}
              </div>
              <Button
                size="sm"
                onClick={handleApplyOcr}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="apply-ocr-btn"
              >
                Appliquer ces informations a la fiche client
              </Button>
            </div>
          )}

          {/* Upload Form */}
          <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
            <p className="text-sm font-medium text-gray-700">Ajouter un document</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type de piece</Label>
                <Select value={idDocForm.doc_type} onValueChange={v => setIdDocForm(f => ({ ...f, doc_type: v }))}>
                  <SelectTrigger data-testid="id-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carte_identite">Carte d'identite</SelectItem>
                    <SelectItem value="carte_nina">Carte NINA</SelectItem>
                    <SelectItem value="carte_biometrique">Carte biometrique</SelectItem>
                    <SelectItem value="passeport">Passeport</SelectItem>
                    <SelectItem value="permis_conduire">Permis de conduire</SelectItem>
                    <SelectItem value="carte_consulaire">Carte consulaire</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">N du document</Label>
                <Input value={idDocForm.doc_number} onChange={e => setIdDocForm(f => ({ ...f, doc_number: e.target.value }))} placeholder="Numero" data-testid="id-doc-number" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Date de naissance</Label>
                <Input type="date" value={idDocForm.date_naissance} onChange={e => setIdDocForm(f => ({ ...f, date_naissance: e.target.value }))} data-testid="id-doc-birth" />
              </div>
              <div>
                <Label className="text-xs">Debut de validite</Label>
                <Input type="date" value={idDocForm.date_debut_validite} onChange={e => setIdDocForm(f => ({ ...f, date_debut_validite: e.target.value }))} data-testid="id-doc-start" />
              </div>
              <div>
                <Label className="text-xs">Fin de validite</Label>
                <Input type="date" value={idDocForm.date_fin_validite} onChange={e => setIdDocForm(f => ({ ...f, date_fin_validite: e.target.value }))} data-testid="id-doc-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optionnel)</Label>
              <Input value={idDocForm.notes} onChange={e => setIdDocForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes..." />
            </div>
            <div>
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors ${uploadingDoc ? 'bg-gray-300 text-gray-500' : 'bg-[#1B3A4B] text-white hover:bg-[#142D3A]'}`}>
                {uploadingDoc ? 'Upload...' : 'Choisir et ajouter le fichier'}
                <input type="file" accept="image/*,.pdf" className="hidden" disabled={uploadingDoc}
                  onChange={e => { if (e.target.files?.[0]) handleUploadIdDoc(e.target.files[0]); e.target.value = ''; }}
                  data-testid="id-doc-file-input" />
              </label>
              <span className="text-xs text-gray-400 ml-2">JPG, PNG, PDF</span>
            </div>
          </div>

          {/* Documents List */}
          <div className="space-y-3 mt-2">
            {loadingDocs ? (
              <p className="text-center text-gray-400 py-4">Chargement...</p>
            ) : idDocs.length === 0 ? (
              <p className="text-center text-gray-400 py-6">Aucune piece justificative</p>
            ) : (
              idDocs.map((doc) => {
                const isExpired = doc.date_fin_validite && new Date(doc.date_fin_validite) < new Date();
                const isImage = ['.jpg', '.jpeg', '.png', '.webp'].includes(doc.file_ext);
                return (
                  <div key={doc.id} className={`border rounded-lg p-3 ${isExpired ? 'border-red-200 bg-red-50/50' : 'border-gray-200'}`} data-testid={`id-doc-${doc.id}`}>
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      {isImage ? (
                        <div className="w-16 h-16 rounded-lg overflow-hidden border cursor-pointer flex-shrink-0 hover:opacity-80"
                          onClick={() => setIdDocPreview(doc.file_url)}>
                          <SecureImage src={doc.file_url} alt={doc.doc_type_label}
                            className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-lg border flex items-center justify-center bg-gray-100 flex-shrink-0 cursor-pointer"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`, '_blank')}>
                          <FileText className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-purple-100 text-purple-700 text-xs">{doc.doc_type_label}</Badge>
                          {doc.doc_number && <span className="text-sm font-mono text-gray-600">N {doc.doc_number}</span>}
                          {doc.validity_status === 'expired' && (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              Expire {doc.days_to_expiry !== null ? `(il y a ${Math.abs(doc.days_to_expiry)}j)` : ''}
                            </Badge>
                          )}
                          {doc.validity_status === 'expiring_soon' && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              Expire bientot (J-{doc.days_to_expiry})
                            </Badge>
                          )}
                          {doc.validity_status === 'valid' && doc.date_fin_validite && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Valide</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                          {doc.date_naissance && <span>Naissance: {new Date(doc.date_naissance).toLocaleDateString('fr-FR')}</span>}
                          {doc.date_debut_validite && <span>Debut: {new Date(doc.date_debut_validite).toLocaleDateString('fr-FR')}</span>}
                          {doc.date_fin_validite && (
                            <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                              Fin: {new Date(doc.date_fin_validite).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                        {doc.notes && <p className="text-xs text-gray-400 mt-0.5">{doc.notes}</p>}
                        <p className="text-xs text-gray-300 mt-0.5">Ajoute par {doc.uploaded_by} le {new Date(doc.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      {/* Delete */}
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 flex-shrink-0"
                        onClick={() => handleDeleteIdDoc(doc.id)} data-testid={`delete-id-doc-${doc.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview */}
      {idDocPreview && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setIdDocPreview(null)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button onClick={() => setIdDocPreview(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg z-10">
              <span className="text-lg font-bold px-1">X</span>
            </button>
            <SecureImage src={idDocPreview} alt="Document"
              className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          </div>
        </div>
      )}

      <AttestationsHistoryModal
        open={!!attestationsHistoryClient}
        client={attestationsHistoryClient}
        onClose={() => setAttestationsHistoryClient(null)}
      />

      {/* Dialogue Mise en demeure + Libération des lieux */}
      <Dialog open={!!tenantNoticeCtx} onOpenChange={(o) => !o && setTenantNoticeCtx(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className={tenantNoticeCtx?.type === 'eviction' ? 'text-rose-700 flex items-center gap-2' : 'text-amber-700 flex items-center gap-2'}>
              {tenantNoticeCtx?.type === 'eviction'
                ? <><LogOut className="w-5 h-5" /> Demande de libération des lieux</>
                : <><AlertTriangle className="w-5 h-5" /> Mise en demeure — retard</>}
            </DialogTitle>
            <DialogDescription>
              {tenantNoticeCtx && <>Locataire : <b>{tenantNoticeCtx.client.name}</b></>}
            </DialogDescription>
          </DialogHeader>

          {tenantNoticeCtx?.type === 'warning' && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Montant en retard (FCFA)</Label>
                  <Input type="number" value={warnForm.amount_overdue}
                         onChange={(e) => setWarnForm({ ...warnForm, amount_overdue: e.target.value })}
                         placeholder="Ex: 175000"
                         data-testid="warn-amount-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Jours de retard</Label>
                  <Input type="number" value={warnForm.days_overdue}
                         onChange={(e) => setWarnForm({ ...warnForm, days_overdue: e.target.value })}
                         placeholder="Ex: 21"
                         data-testid="warn-days-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Délai de règlement laissé (jours)</Label>
                <Input type="number" value={warnForm.grace_period_days}
                       onChange={(e) => setWarnForm({ ...warnForm, grace_period_days: e.target.value })}
                       data-testid="warn-grace-input" />
                <p className="text-[11px] text-gray-500 mt-1">
                  À défaut de règlement dans ce délai, une demande de libération pourra être engagée.
                </p>
              </div>
            </div>
          )}

          {tenantNoticeCtx?.type === 'eviction' && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-semibold">Motif *</Label>
                <select
                  value={evictForm.motif}
                  onChange={(e) => setEvictForm({ ...evictForm, motif: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="evict-motif-select"
                >
                  <option value="unpaid_rent">Loyers impayés</option>
                  <option value="lease_end">Fin de bail</option>
                  <option value="contract_breach">Non-respect des engagements</option>
                  <option value="other">Autre (préciser)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">
                  Détails / justification {evictForm.motif === 'other' && <span className="text-rose-600">*</span>}
                </Label>
                <Textarea rows={4} value={evictForm.motif_detail}
                          onChange={(e) => setEvictForm({ ...evictForm, motif_detail: e.target.value })}
                          placeholder="Ex: Loyers impayés depuis 3 mois malgré 2 relances..."
                          data-testid="evict-detail-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Délai de libération (jours)</Label>
                <Input type="number" value={evictForm.vacate_deadline_days}
                       onChange={(e) => setEvictForm({ ...evictForm, vacate_deadline_days: e.target.value })}
                       data-testid="evict-deadline-input" />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setTenantNoticeCtx(null)}>Annuler</Button>
            <Button
              onClick={submitTenantNotice}
              className={tenantNoticeCtx?.type === 'eviction' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="tenant-notice-generate-btn"
            >
              Générer le PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
